import React, { useCallback, useMemo, useRef } from 'react'
import XLSX from 'xlsx'

//UI
import { Box, Button, Flex, SimpleGrid, Spacer, Text } from '@chakra-ui/react'

import { NumberInput } from '@/components/Form'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import useZZZ6 from '@/features/zzz6/useZZZ6'

import { excelFileTypes } from '@/utils/constants'

//Hooks

//Hooks

import { createExcelWithOptions } from '@/utils/fileHelper'
import { isNullish, isNumber } from '@/utils/helper'
import { numberToColumnKey, roundNumber } from '@/utils/stringHelper'
import {
  CostBudgetType,
  COST_BUDGET_HEADER_MAPPING,
  getQuotationDetailTableColumnIds,
  OfferQuotationDetailInstallAndSupplyUnit,
  OfferQuotationDetailMaintenanceUnit,
  OfferQuotationTemplateScopeOfWork,
} from '@eapproval/offer-shared'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { createDefaultQuotationDetailRow } from '../VendorUploadPage.helper'
import VendorQuotationDetailTable from './VendorQuotationDetailTable'
import { getQuotationDetailExcelColumnIds, validateImportedData } from './quotationTeamplateDetailColumns'

const getDefaultCategoryByCostBudgetType = (costBudgetType) => {
  if (costBudgetType === CostBudgetType.SUPPLY_ENGINEERING_SERVICE) {
    return 'General Electrical Installation'
  }

  if (costBudgetType === CostBudgetType.SUPPLY_MAINTENANCE_SERVICE) {
    return 'Maintenance Building'
  }

  return ''
}

const VendorUploadQuotationDetailSection = ({ canUpload, RFQDetail }) => {
  const { t, i18n } = useTranslation()
  const { setValue, getValues } = useFormContext()
  const importExcelRef = useRef(null)
  const { useSystemVariableOptions } = useSystemVariable()
  const { useFetchZZZ6s } = useZZZ6()
  const { data: hsCodeOptions } = useSystemVariableOptions('hsCode', {
    status: 'active',
    labelKey: (row) => `${row.code}`,
  })
  const { data: zzz6Data } = useFetchZZZ6s({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })

  const quotationDetails = useWatch({ name: 'quotationDetails' }) ?? []
  const discountAmount = useWatch({ name: 'discountAmount' }) ?? 0
  const quotationAmount = useWatch({ name: 'amount' }) ?? 0
  const quotationDetailTemplate = useWatch({ name: 'quotationDetailTemplate' }) ?? 0
  const costBudgetType = RFQDetail?.type

  const {
    fields: quotationDetailFields,
    append: appendQuotationDetail,
    remove: removeQuotationDetail,
  } = useFieldArray({ name: 'quotationDetails' })

  const totalQuotationPrice = useMemo(() => {
    if (!Array.isArray(quotationDetails)) {
      return 0
    }

    const total = quotationDetails.reduce((acc, quoteDetail) => {
      return acc + parseFloat(isNumber(quoteDetail?.totalPrice) ? quoteDetail?.totalPrice : 0)
    }, 0)

    return roundNumber(total, 2)
  }, [quotationDetails])

  const handleAppendQuotationDetail = () => {
    appendQuotationDetail(createDefaultQuotationDetailRow(costBudgetType, zzz6Data?.data ?? []))
  }

  const handleImportExcel = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const data = e.target.result
      const workbook = XLSX.read(data, { type: 'binary', cellDates: true })
      const sheetName = workbook.SheetNames[0]
      const sheet = workbook.Sheets[sheetName]
      const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 })
      const header = sheetData[0]

      const tableColumns = getQuotationDetailExcelColumnIds(costBudgetType)
      const defaultCategory = getDefaultCategoryByCostBudgetType(costBudgetType)

      //Validate sheet data

      const dataRows = sheetData
        .slice(1) //Remove the header row
        .map((row) => {
          let obj = {}

          header.forEach((key, i) => {
            const rowValue = row[i]

            if (isNullish(rowValue) || rowValue === '') {
              obj[tableColumns[i]] = ''
            } else if (tableColumns[i].includes('Date')) {
              // Handle date: extract year, month, day to ignore timezone completely
              if (rowValue instanceof Date) {
                const year = rowValue.getUTCFullYear()
                const month = rowValue.getUTCMonth()
                const day = rowValue.getUTCDate()
                obj[tableColumns[i]] = new Date(year, month, day)
              } else {
                obj[tableColumns[i]] = ''
              }
            } else {
              obj[tableColumns[i]] = !isNaN(rowValue) ? parseFloat(rowValue) : rowValue
            }
          })

          if (defaultCategory && Object.prototype.hasOwnProperty.call(obj, 'category') && !obj.category) {
            obj.category = defaultCategory
          }

          return obj
        })

      //Validate if the data is correct

      try {
        validateImportedData(dataRows, quotationDetailTemplate)
      } catch (err) {
        toast.error(err.message)
        return
      }

      setValue('quotationDetails', dataRows)
    }
    reader.readAsBinaryString(file)
    if (importExcelRef.current) importExcelRef.current.value = ''
  }

  const handleDownloadQuotationDetail = useCallback(async () => {
    try {
      const quotationDetails = getValues('quotationDetails') ?? []

      //Use To determine if the quotation table need to include dropdown
      const quotationDetailColumns = getQuotationDetailTableColumnIds({
        costBudgetType,
        isTemplate: false,
        isExcel: true,
      })

      const header = quotationDetailColumns.map((col) => {
        //find header

        let header = '(-)'
        if (Object.keys(COST_BUDGET_HEADER_MAPPING).includes(col)) {
          header = COST_BUDGET_HEADER_MAPPING[col]
        }
        return {
          header: header,
          key: col,
          width: 10,
        }
      })

      const optionConfigs = []

      if (quotationDetailColumns.includes('scopeOfWorkSelect')) {
        optionConfigs.push({
          options: Object.values(OfferQuotationTemplateScopeOfWork),
          columnKey: numberToColumnKey(quotationDetailColumns.findIndex((col) => col === 'scopeOfWorkSelect') + 1),
        })
      }

      if (quotationDetailColumns.includes('unitSelect')) {
        optionConfigs.push({
          options: Object.values(OfferQuotationDetailInstallAndSupplyUnit),
          columnKey: numberToColumnKey(quotationDetailColumns.findIndex((col) => col === 'unitSelect') + 1),
        })
      }
      if (quotationDetailColumns.includes('unitMaintenance')) {
        optionConfigs.push({
          options: Object.values(OfferQuotationDetailMaintenanceUnit),
          columnKey: numberToColumnKey(quotationDetailColumns.findIndex((col) => col === 'unitMaintenance') + 1),
        })
      }

      if (quotationDetailColumns.includes('HSCode')) {
        optionConfigs.push({
          options: (hsCodeOptions ?? []).map((option) => option.value),
          columnKey: numberToColumnKey(quotationDetailColumns.findIndex((col) => col === 'HSCode') + 1),
        })
      }

      const formattedData = quotationDetails.map((row) => {
        const newRow = {}
        Object.keys(row).forEach((key) => {
          if (key.includes('Date') || key.includes('date') || key.includes('createdAt') || key.includes('updatedAt')) {
            if (row[key] instanceof Date) {
              newRow[key] = moment(row[key]).format('DD-MMM-YYYY')
            } else {
              newRow[key] = row[key]
            }
          } else {
            newRow[key] = row[key]
          }
        })

        return newRow
      })

      const excelBlob = await createExcelWithOptions(header, formattedData, optionConfigs)
      saveAs(excelBlob, `E-Approval Quotation Detail-${Date.now()}.xlsx`)
    } catch (err) {
      console.log(err)
    }
  }, [costBudgetType, getValues, hsCodeOptions])

  const handleDiscountRateChange = (newDiscountRate) => {
    const discountedPrice = roundNumber((totalQuotationPrice * newDiscountRate) / 100, 2)
    setValue('discountAmount', discountedPrice, { shouldDirty: true })
  }

  const handleDiscountAmountChange = (newDiscountAmount) => {
    if (discountAmount > totalQuotationPrice) {
      return
    }
    const discountRate = roundNumber((newDiscountAmount / totalQuotationPrice) * 100, 2)
    setValue('discountRate', discountRate, { shouldDirty: true })
  }

  return (
    <>
      <Flex gap="2" alignItems={'center'}>
        <Text size={'lg'} color={'blue.800'} fontWeight={'semibold'}>
          {t('VENDOR_UPLOAD_PAGE_DATA_TABLE')}
        </Text>
        <Spacer />
        <Text size={'md'} fontWeight={'semibold'}>
          {t('VENDOR_UPLOAD_PAGE_DATA_TABLE_UPLOAD')}
        </Text>
        <input
          type="file"
          ref={importExcelRef}
          accept={excelFileTypes}
          disabled={!canUpload}
          onChange={handleImportExcel}
        />

        <Button colorScheme={'blue'} onClick={handleAppendQuotationDetail} disabled={!canUpload}>
          {t('VENDOR_UPLOAD_PAGE_DATA_TABLE_ADDROW')}
        </Button>
        <Button colorScheme={'green'} onClick={handleDownloadQuotationDetail}>
          {t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DOWNLOAD')}
        </Button>
      </Flex>

      <VendorQuotationDetailTable
        removeQuotationDetail={removeQuotationDetail}
        data={quotationDetails}
        disabled={!canUpload}
        costBudgetType={RFQDetail?.type}
        template={quotationDetailTemplate}
      />

      <SimpleGrid columns="2" spacing={'2'}>
        <NumberInput
          label={t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DISCOUNT_AMOUNT')}
          name="discountAmount"
          isRequired={false}
          isRow
          placeholder="0.00"
          disabled={!canUpload}
          // extraOnChange={handleDiscountAmountChange}
          isAllowValidator={({ floatValue }) => {
            if (isNullish(floatValue)) return true
            return floatValue < totalQuotationPrice
          }}
        />
        <Flex align={'center'} w="50%">
          <Text color={'blue.800'} fontWeight={'semibold'} w="32%">
            {t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DISCOUNTED_AMOUNT')}
          </Text>
          <Text color={'blue.800'} fontWeight={'semibold'}>
            {`$${roundNumber(totalQuotationPrice - Number(discountAmount || 0), 2)}`}
          </Text>
        </Flex>
      </SimpleGrid>
    </>
  )
}
export default VendorUploadQuotationDetailSection
